// History API routing helper. Replaces fragment-based navigation with clean
// pathnames. Call installNavInterceptor() once at boot; it captures all
// same-origin anchor clicks and routes them via pushState + dispatches a
// 'route:change' CustomEvent the main router listens for.

export function currentPath(): string {
  const p = window.location.pathname;
  return p === '' ? '/' : p;
}

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  const next = path.startsWith('/') ? path : `/${path}`;
  if (next === currentPath() + window.location.search) return;
  if (opts.replace) window.history.replaceState({}, '', next);
  else window.history.pushState({}, '', next);
  window.dispatchEvent(new CustomEvent('route:change'));
}

export function onRouteChange(handler: () => void): void {
  window.addEventListener('route:change', handler);
  window.addEventListener('popstate', handler);
}

export function installNavInterceptor(): void {
  document.addEventListener('click', (ev) => {
    if (ev.defaultPrevented) return;
    if (ev.button !== 0) return;
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    const target = ev.target as Element | null;
    if (!target) return;

    // 1) Anchor navigation.
    const anchor = target.closest('a');
    if (anchor) {
      const href = anchor.getAttribute('href');
      if (!href) return;
      // Skip external + non-internal + opens-in-new-tab + downloads
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;
      if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:')) return;
      // Allow #-prefixed hrefs to behave normally (legacy/in-page anchors)
      if (href.startsWith('#')) return;
      if (!href.startsWith('/')) return;
      ev.preventDefault();
      navigate(href);
      return;
    }

    // 2) Row-level navigation: an element with data-row-href (typically a <tr>)
    //    is clickable across its whole surface for a bigger hit area. Clicks on
    //    interactive controls inside the row (checkboxes, buttons, secondary
    //    links handled above) are left alone so they keep their own behaviour.
    const row = target.closest('[data-row-href]');
    if (!row) return;
    if (target.closest('button, input, select, textarea, label, [data-no-row-nav]')) return;
    const rowHref = row.getAttribute('data-row-href');
    if (!rowHref || !rowHref.startsWith('/')) return;
    ev.preventDefault();
    navigate(rowHref);
  });
}
